import { trackEvent } from '@/utils/analytics';
import { ContactRequestError, requestContactJSON, withContactDeadline } from '@/utils/contact-api';
import React, { useEffect, useRef, useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface ContactFormProps {
    variant?: 'page' | 'component';
}

interface ContactFormData {
    name: string;
    email: string;
    subject: string;
    message: string;
}

interface SubmitState {
    status: 'idle' | 'loading' | 'success' | 'error';
    error?: string;
}

interface TouchedFields {
    name: boolean;
    email: boolean;
    subject: boolean;
    message: boolean;
}

const DRAFT_KEY = 'portfolio_contact_draft';
const emptyForm: ContactFormData = { name: '', email: '', subject: '', message: '' };
const untouched: TouchedFields = { name: false, email: false, subject: false, message: false };

function readDraft(): ContactFormData {
    try {
        const draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null');
        if (draft && Object.keys(emptyForm).every(key => typeof draft[key] === 'string')) return draft;
    } catch {
        /* Storage can be unavailable or contain an invalid draft. */
    }
    return { ...emptyForm };
}

interface ContactConfig {
    recaptchaRequired: boolean;
    contactAvailable: boolean;
}
interface ContentProps extends ContactFormProps {
    config: ContactConfig | null;
    configError?: string;
    retryConfig: () => void;
    executeRecaptcha?: (action: string) => Promise<string>;
}

const ContactFormContent: React.FC<ContentProps> = ({
    variant = 'component',
    config,
    configError,
    retryConfig,
    executeRecaptcha,
}) => {
    const [formData, setFormData] = useState<ContactFormData>(readDraft);
    useEffect(() => {
        try {
            if (Object.values(formData).some(Boolean)) sessionStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
            else sessionStorage.removeItem(DRAFT_KEY);
        } catch {
            /* Sending a message must remain possible without draft storage. */
        }
    }, [formData]);

    const [submitState, setSubmitState] = useState<SubmitState>({ status: 'idle' });
    const [touchedFields, setTouchedFields] = useState<TouchedFields>({
        name: false,
        email: false,
        subject: false,
        message: false,
    });
    const csrfToken = useRef<string | null>(null);
    const submitting = useRef(false);
    const lifetime = useRef<AbortController | null>(null);
    const [retryAt, setRetryAt] = useState(0);
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const controller = new AbortController();
        lifetime.current = controller;
        return () => controller.abort();
    }, []);
    useEffect(() => {
        if (!retryAt) return;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [retryAt]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Track form started on first input
        if (!touchedFields.name && !touchedFields.email && !touchedFields.subject && !touchedFields.message) {
            trackEvent('contact_form_started');
        }

        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        // Mark field as touched
        setTouchedFields(prev => ({
            ...prev,
            [name]: true,
        }));

        // Clear error message when user starts typing
        if (submitState.status === 'error') {
            setSubmitState({ status: 'idle' });
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name } = e.target;
        setTouchedFields(prev => ({
            ...prev,
            [name]: true,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const errors = [
            validateName(formData.name),
            validateEmail(formData.email),
            validateSubject(formData.subject),
            validateMessage(formData.message),
        ];
        setTouchedFields({ name: true, email: true, subject: true, message: true });
        const invalidIndex = errors.findIndex(Boolean);
        if (invalidIndex >= 0) {
            const field = ['name', 'email', 'subject', 'message'][invalidIndex];
            e.currentTarget.querySelector<HTMLElement>(`[name="${field}"]`)?.focus();
            return;
        }
        if (submitting.current || !canSubmit || !lifetime.current) return;
        submitting.current = true;
        const signal = lifetime.current.signal;
        setSubmitState({ status: 'loading' });

        try {
            let token = csrfToken.current;
            if (!token) {
                const result = await requestContactJSON('/api/csrf-token', {}, signal);
                if (typeof result.csrfToken !== 'string' || !result.csrfToken) {
                    throw new ContactRequestError('Could not prepare your message. Please try again.');
                }
                token = result.csrfToken;
                csrfToken.current = token;
            }
            let recaptchaToken: string | undefined;
            if (config?.recaptchaRequired) {
                if (!executeRecaptcha) {
                    throw new ContactRequestError('Verification is still loading. Please wait a moment and try again.');
                }
                try {
                    recaptchaToken = await withContactDeadline(
                        () => executeRecaptcha('contact_form'),
                        10000,
                        signal,
                        'Verification timed out. Please try again.'
                    );
                } catch (error) {
                    if (error instanceof ContactRequestError || signal.aborted) throw error;
                    throw new ContactRequestError('Verification failed to load. Please try again.');
                }
            }
            const result = await requestContactJSON(
                '/api/contact',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-csrf-token': token },
                    body: JSON.stringify({ ...formData, email: formData.email.trim(), recaptchaToken }),
                },
                signal,
                true
            );
            if (result.success !== true) {
                throw new ContactRequestError(
                    typeof result.message === 'string'
                        ? result.message
                        : 'The server returned an unexpected response. Please try again.'
                );
            }
            if (signal.aborted) return;
            setSubmitState({ status: 'success' });
            setFormData({ ...emptyForm });
            setTouchedFields({ ...untouched });
            trackEvent('contact_form_submitted', { success: true });
        } catch (error) {
            if (signal.aborted) return;
            let message = error instanceof Error ? error.message : 'Failed to send message. Please try again.';
            if (error instanceof ContactRequestError) {
                if (error.status === 403 && error.code === 'CSRF_INVALID') {
                    csrfToken.current = null;
                    message = 'Your session token was rejected. Your draft is saved; please send again.';
                }
                if (error.status === 429) {
                    const until = Number.isFinite(error.retryAt) ? error.retryAt! : Date.now() + 15 * 60 * 1000;
                    setRetryAt(until);
                    setNow(Date.now());
                    message = 'Too many contact attempts. Your draft is saved. Please wait before trying again.';
                }
            }
            setSubmitState({ status: 'error', error: message });
            trackEvent('contact_form_submitted', { success: false });
        } finally {
            submitting.current = false;
        }
    };

    // Validation functions
    const validateName = (name: string) => {
        const trimmed = name.trim();
        if (trimmed.length < 2) return 'Name must be at least 2 characters';
        if (trimmed.length > 50) return 'Name must be 50 characters or less';
        if (!/^[a-zA-Z0-9\s\-'\.]+$/.test(trimmed)) {
            return 'Name can only contain letters, numbers, spaces, hyphens, apostrophes, and periods';
        }
        return null;
    };

    const validateEmail = (email: string) => {
        const trimmed = email.trim();
        if (!trimmed) return 'Email is required';
        if (trimmed.length > 254) return 'Email must be 254 characters or less';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            return 'Please enter a valid email address';
        }
        return null;
    };

    const validateSubject = (subject: string) => {
        if (subject && subject.length > 200) {
            return 'Subject must be 200 characters or less';
        }
        return null;
    };

    const validateMessage = (message: string) => {
        const trimmed = message.trim();
        if (!trimmed) return 'Message is required';
        if (trimmed.length < 5) return 'Message must be at least 5 characters';
        if (message.length > 2000) return 'Message must be 2000 characters or less';
        return null;
    };

    // Get validation errors (only show if field has been touched)
    const nameError = touchedFields.name ? validateName(formData.name) : null;
    const emailError = touchedFields.email ? validateEmail(formData.email) : null;
    const subjectError = touchedFields.subject ? validateSubject(formData.subject) : null;
    const messageError = touchedFields.message ? validateMessage(formData.message) : null;

    const isSubmitting = submitState.status === 'loading';
    const waitSeconds = Math.max(0, Math.ceil((retryAt - now) / 1000));
    const canSubmit = Boolean(config?.contactAvailable) && !configError && !isSubmitting && waitSeconds === 0;

    const isPageVariant = variant === 'page';

    return (
        <div className={`bg-vscode-bg text-vscode-text ${isPageVariant ? 'min-h-screen' : 'p-6'}`}>
            <div className={`max-w-2xl mx-auto ${isPageVariant ? 'p-8' : ''}`}>
                {/* Header */}
                <div className='text-center mb-8'>
                    <h1 className={`font-bold text-vscode-accent ${isPageVariant ? 'text-4xl' : 'text-3xl'}`}>
                        Get In Touch
                    </h1>
                </div>

                {/* Status Messages */}
                {submitState.status === 'success' && (
                    <div
                        role='status'
                        aria-live='polite'
                        className='text-center p-4 bg-green-900/20 border border-green-600/30 rounded-lg mb-6'
                    >
                        <p className='text-green-400 font-medium'>Message sent successfully!</p>
                        <p className='text-green-400/80 text-sm mt-1'>I'll get back to you soon.</p>
                    </div>
                )}
                {submitState.status === 'error' && (
                    <div
                        role='alert'
                        className='text-center p-4 bg-red-900/20 border border-red-600/30 rounded-lg mb-6'
                    >
                        <p className='text-red-400 font-medium'>Failed to send message.</p>
                        <p className='text-red-400/80 text-sm mt-1'>{submitState.error || 'Please try again.'}</p>
                    </div>
                )}

                {!config && !configError && (
                    <p role='status' className='mb-6'>
                        Checking contact availability…
                    </p>
                )}
                {configError && (
                    <div role='alert' className='mb-6 text-red-400'>
                        <p>{configError}</p>
                        <button type='button' onClick={retryConfig} className='mt-2 underline'>
                            Try again
                        </button>
                    </div>
                )}
                {config && !config.contactAvailable && (
                    <p role='status' className='mb-6'>
                        Contact is temporarily unavailable. Your draft will stay saved.
                    </p>
                )}
                {waitSeconds > 0 && (
                    <p role='status' className='mb-6'>
                        Try again in {waitSeconds} seconds.
                    </p>
                )}
                {/* Contact Form */}
                <form onSubmit={handleSubmit} noValidate aria-busy={isSubmitting} className='space-y-6'>
                    <p className='text-sm text-vscode-text-secondary'>
                        Fields marked * are required. Your draft stays in this browser tab until sent or cleared.
                    </p>
                    {/* Name Field */}
                    <div>
                        <label htmlFor='name' className='block text-sm font-medium text-vscode-text mb-2'>
                            Your Name *
                        </label>
                        <input
                            type='text'
                            id='name'
                            autoComplete='name'
                            maxLength={50}
                            name='name'
                            aria-invalid={Boolean(nameError)}
                            aria-describedby={`name-help${nameError ? ' name-error' : ''}`}
                            value={formData.name}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            className={`w-full px-4 py-3 bg-vscode-bg-secondary border rounded-lg text-vscode-text placeholder-vscode-text-muted focus:outline-none focus:ring-1 transition-colors ${
                                nameError
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                    : 'border-gray-600/30 focus:border-vscode-accent focus:ring-vscode-accent'
                            }`}
                            placeholder='Enter your name'
                        />
                        <p id='name-help' className='text-vscode-text-secondary text-sm mt-1'>
                            2–50 characters; letters, numbers, spaces, hyphens, apostrophes, and periods.
                        </p>
                        {nameError && (
                            <p id='name-error' role='alert' className='text-red-400 text-sm mt-1'>
                                {nameError}
                            </p>
                        )}
                    </div>

                    {/* Email Field */}
                    <div>
                        <label htmlFor='email' className='block text-sm font-medium text-vscode-text mb-2'>
                            Your Email *
                        </label>
                        <input
                            type='email'
                            id='email'
                            autoComplete='email'
                            inputMode='email'
                            maxLength={254}
                            name='email'
                            aria-invalid={Boolean(emailError)}
                            aria-describedby={`email-help${emailError ? ' email-error' : ''}`}
                            value={formData.email}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            className={`w-full px-4 py-3 bg-vscode-bg-secondary border rounded-lg text-vscode-text placeholder-vscode-text-muted focus:outline-none focus:ring-1 transition-colors ${
                                emailError
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                    : 'border-gray-600/30 focus:border-vscode-accent focus:ring-vscode-accent'
                            }`}
                            placeholder='Enter your email address'
                        />
                        <p id='email-help' className='text-vscode-text-secondary text-sm mt-1'>
                            Use an email address where you can receive a reply.
                        </p>
                        {emailError && (
                            <p id='email-error' role='alert' className='text-red-400 text-sm mt-1'>
                                {emailError}
                            </p>
                        )}
                    </div>

                    {/* Subject Field */}
                    <div>
                        <label htmlFor='subject' className='block text-sm font-medium text-vscode-text mb-2'>
                            Subject <span className='text-vscode-text-muted'>(Optional)</span>
                        </label>
                        <input
                            type='text'
                            id='subject'
                            maxLength={200}
                            name='subject'
                            aria-invalid={Boolean(subjectError)}
                            aria-describedby={`subject-help${subjectError ? ' subject-error' : ''}`}
                            value={formData.subject}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            className={`w-full px-4 py-3 bg-vscode-bg-secondary border rounded-lg text-vscode-text placeholder-vscode-text-muted focus:outline-none focus:ring-1 transition-colors ${
                                subjectError
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                    : 'border-gray-600/30 focus:border-vscode-accent focus:ring-vscode-accent'
                            }`}
                            placeholder='What is this regarding?'
                        />
                        <p id='subject-help' className='text-vscode-text-secondary text-sm mt-1'>
                            Optional, up to 200 characters.
                        </p>
                        {subjectError && (
                            <p id='subject-error' role='alert' className='text-red-400 text-sm mt-1'>
                                {subjectError}
                            </p>
                        )}
                    </div>

                    {/* Message Field */}
                    <div>
                        <label htmlFor='message' className='block text-sm font-medium text-vscode-text mb-2'>
                            Message *
                        </label>
                        <textarea
                            id='message'
                            minLength={5}
                            maxLength={2000}
                            name='message'
                            aria-invalid={Boolean(messageError)}
                            aria-describedby={`message-help${messageError ? ' message-error' : ''}`}
                            value={formData.message}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            rows={6}
                            className={`w-full px-4 py-3 bg-vscode-bg-secondary border rounded-lg text-vscode-text placeholder-vscode-text-muted focus:outline-none focus:ring-1 transition-colors resize-vertical ${
                                messageError
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                    : 'border-gray-600/30 focus:border-vscode-accent focus:ring-vscode-accent'
                            }`}
                            placeholder='Tell me about your project or question...'
                        />
                        <p id='message-help' className='text-vscode-text-secondary text-sm mt-1'>
                            5–2000 characters. {formData.message.length}/2000
                        </p>
                        {messageError && (
                            <p id='message-error' role='alert' className='text-red-400 text-sm mt-1'>
                                {messageError}
                            </p>
                        )}
                    </div>

                    {isSubmitting && (
                        <p role='status' className='text-vscode-text-secondary text-sm'>
                            Sending your message…
                        </p>
                    )}
                    {/* Submit Button */}
                    <div className='text-center'>
                        <button
                            type='submit'
                            disabled={!canSubmit}
                            className={`px-8 py-3 font-medium rounded-lg transition-colors duration-200 ${
                                canSubmit
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                        </button>
                        {Object.values(formData).some(Boolean) && (
                            <button
                                type='button'
                                disabled={isSubmitting}
                                onClick={() => {
                                    setFormData({ ...emptyForm });
                                    setTouchedFields({ ...untouched });
                                    setSubmitState({ status: 'idle' });
                                }}
                                className='ml-4 px-3 py-3 text-vscode-text-secondary underline'
                            >
                                Clear draft
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

const VerifiedContactForm: React.FC<Omit<ContentProps, 'executeRecaptcha'>> = props => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    return <ContactFormContent {...props} executeRecaptcha={executeRecaptcha} />;
};

export const ContactForm: React.FC<ContactFormProps> = ({ variant = 'component' }) => {
    const [config, setConfig] = useState<ContactConfig | null>(null);
    const [configError, setConfigError] = useState<string>();
    const [attempt, setAttempt] = useState(0);
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITEKEY?.trim();
    useEffect(() => {
        const controller = new AbortController();
        setConfig(null);
        setConfigError(undefined);
        void requestContactJSON('/api/contact-config', {}, controller.signal)
            .then(result => {
                if (controller.signal.aborted) return;
                if (typeof result.recaptchaRequired !== 'boolean' || typeof result.contactAvailable !== 'boolean') {
                    throw new ContactRequestError('Could not check contact availability. Please try again.');
                }
                setConfig({ recaptchaRequired: result.recaptchaRequired, contactAvailable: result.contactAvailable });
                if (result.recaptchaRequired && !siteKey) {
                    setConfigError('Contact verification is unavailable. Please try again later.');
                }
            })
            .catch(error => {
                if (!controller.signal.aborted)
                    setConfigError(error instanceof Error ? error.message : 'Contact is unavailable.');
            });
        return () => controller.abort();
    }, [attempt, siteKey]);
    const props: ContentProps = { variant, config, configError, retryConfig: () => setAttempt(value => value + 1) };
    if (config?.recaptchaRequired && siteKey) {
        return (
            <GoogleReCaptchaProvider
                reCaptchaKey={siteKey}
                scriptProps={{ async: true, defer: true, appendTo: 'head' }}
            >
                <VerifiedContactForm {...props} />
            </GoogleReCaptchaProvider>
        );
    }
    return <ContactFormContent {...props} />;
};
